import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { imgSrc } from '../../lib/env';
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const FALLBACK = [
  {
    id: 0,
    title: 'Timeless Elegance',
    subtitle: 'Parisian Exclusives',
    img: '',
    cta: 'Explore Brands',
    ctaLink: '/shop',
  },
];

export default function HeroSlider({ banners = null }) {
  // const slides              = banners.length ? banners : FALLBACK;
  const isLoading = banners === null;
  const slides = !isLoading && banners.length ? banners : FALLBACK;
  const [idx, setIdx]       = useState(0);
  const [paused, setPaused] = useState(false);
  const isMobile            = useIsMobile();

  const prev = useCallback(() => setIdx(i => (i - 1 + slides.length) % slides.length), [slides.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % slides.length),                 [slides.length]);

  useEffect(() => {
    if (slides.length < 2 || paused) return;
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [slides.length, paused, next]);

  // Touch swipe support for mobile
  const [touchStart, setTouchStart] = useState(null);
  function onTouchStart(e) { setTouchStart(e.touches[0].clientX); }
  function onTouchEnd(e) {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    setTouchStart(null);
  }

  if (isLoading) {
    return (
      <div style={{
        height: '100svh',
        minHeight: 480,
        background: 'var(--dark)',
      }} />
    );
  }


  const s  = slides[idx];
  const bg = imgSrc(s.img);

  const titleWords = (s.title || 'Timeless Elegance').split(' ');
  const firstWord  = titleWords[0];
  const restWords  = titleWords.slice(1).join(' ');
    // ✅ ADD HERE
  if (isLoading) {
    return (
      <div style={{
        height: '100svh',
        minHeight: 480,
        background: 'var(--dark)',
      }} />
    );
  }

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'relative',
        // Shorter on mobile — full viewport on desktop
        // height: isMobile ? '100vh' : '100vh',
        height: '100svh',     
        minHeight: isMobile ? 480 : 560,
        overflow: 'hidden',
        background: 'var(--dark)',
      }}
    >
      {/* Background image */}
      {bg
        ? <img
            src={bg}
            alt={s.title || ''}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              // cover keeps image filled; on mobile anchor to top so face/subject stays visible
              objectFit: 'cover',
              objectPosition: isMobile ? 'center top' : 'center center',
              opacity: 0.65,
              transition: 'opacity 0.8s',
            }}
          />
        : <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(135deg,#1a1a1a 0%,#2d2420 50%,#1a1a1a 100%)',
          }} />
      }

      {/* Overlay — slightly darker on mobile for text legibility */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isMobile ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.25)',
      }} />

      {/* Content */}
      <div style={{
        position: 'relative', zIndex: 10, height: '100%',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        textAlign: 'center',
        padding: isMobile ? '0 24px' : '0 40px',
      }}>

        {/* Subtitle label */}
        <div style={{
          fontSize: isMobile ? 10 : 11,
          letterSpacing: '0.35em',
          color: 'rgba(255,255,255,0.75)',
          textTransform: 'uppercase',
          fontWeight: 500,
          marginBottom: isMobile ? 16 : 24,
          fontFamily: 'var(--font-body)',
        }}>
          {s.subtitle || 'Aurora Luxury India'}
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 300,
          letterSpacing: '-0.02em',
          lineHeight: 1.05,
          margin: isMobile ? '0 0 32px' : '0 0 48px',
          // Tighter range on mobile so it never overflows
          fontSize: isMobile ? 'clamp(40px,12vw,72px)' : 'clamp(56px,10vw,110px)',
        }}>
          <span style={{ display: 'block', fontStyle: 'normal', color: '#fff' }}>
            {firstWord}
          </span>
          {restWords && (
            <span style={{ display: 'block', fontStyle: 'italic', color: 'var(--gold)' }}>
              {restWords}
            </span>
          )}
        </h1>

        {/* CTA Button */}
        <Link
          to={s.ctaLink || '/shop'}
          style={{
            padding: isMobile ? '14px 36px' : '16px 52px',
            background: 'transparent',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.6)',
            fontSize: isMobile ? 10 : 11,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'all 0.3s',
            display: 'inline-block',
            // On mobile make it slightly wider for easy tapping
            minWidth: isMobile ? 180 : 'auto',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background  = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.borderColor = '#fff';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background  = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
          }}>
          {s.cta || 'Explore Brands'}
        </Link>

        {/* Swipe hint — mobile only, fades after a second */}
        {/* {isMobile && slides.length > 1 && (
          <div style={{
            marginTop: 28,
            fontSize: 10,
            letterSpacing: '0.15em',
            color: 'rgba(255,255,255,0.35)',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-body)',
          }}>
            Swipe to browse
          </div>
        )} */}
      </div>

      {/* Dots */}
      {slides.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: isMobile ? 20 : 28,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', gap: 8, zIndex: 10,
        }}>
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{
              height: 2,
              width: i === idx ? 32 : 14,
              background: i === idx ? '#fff' : 'rgba(255,255,255,0.3)',
              border: 'none', cursor: 'pointer',
              transition: 'all 0.35s', padding: 0,
            }} />
          ))}
        </div>
      )}

      {/* Arrows — hidden on mobile (use swipe instead) */}
      {slides.length > 1 && !isMobile && (
        <>
          <button
            onClick={prev}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            style={{
              position: 'absolute', left: 20, top: '50%',
              transform: 'translateY(-50%)', zIndex: 10,
              width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              cursor: 'pointer', color: 'rgba(255,255,255,0.7)',
              transition: 'all 0.2s',
            }}
            onFocus={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onBlur={e  => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          >
            <svg width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            onClick={next}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            style={{
              position: 'absolute', right: 20, top: '50%',
              transform: 'translateY(-50%)', zIndex: 10,
              width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              cursor: 'pointer', color: 'rgba(255,255,255,0.7)',
              transition: 'all 0.2s',
            }}
            onFocus={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
            onBlur={e  => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          >
            <svg width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}

// import { useState, useEffect, useCallback } from 'react';
// import { Link } from 'react-router-dom';
// import { imgSrc } from '../../lib/env';

// function useIsMobile(breakpoint = 768) {
//   const [isMobile, setIsMobile] = useState(
//     typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
//   );
//   useEffect(() => {
//     const handler = () => setIsMobile(window.innerWidth < breakpoint);
//     window.addEventListener('resize', handler);
//     return () => window.removeEventListener('resize', handler);
//   }, [breakpoint]);
//   return isMobile;
// }

// const FALLBACK = [
//   {
//     id: 0,
//     title: 'Timeless Elegance',
//     subtitle: 'Parisian Exclusives',
//     img: '',
//     cta: 'Explore Brands',
//     ctaLink: '/shop',
//   },
// ];

// export default function HeroSlider({ banners = null }) {
//   const isLoading = banners === null;
//   const slides    = !isLoading && banners.length ? banners : FALLBACK;
//   const [idx, setIdx]       = useState(0);
//   const [paused, setPaused] = useState(false);
//   const isMobile            = useIsMobile();

//   const prev = useCallback(() => setIdx(i => (i - 1 + slides.length) % slides.length), [slides.length]);
//   const next = useCallback(() => setIdx(i => (i + 1) % slides.length), [slides.length]);

//   useEffect(() => {
//     if (slides.length < 2 || paused) return;
//     const t = setInterval(next, 5500);
//     return () => clearInterval(t);
//   }, [slides.length, paused, next]);

//   const [touchStart, setTouchStart] = useState(null);
//   function onTouchStart(e) { setTouchStart(e.touches[0].clientX); }
//   function onTouchEnd(e) {
//     if (touchStart === null) return;
//     const diff = touchStart - e.changedTouches[0].clientX;
//     if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
//     setTouchStart(null);
//   }

//   if (isLoading) {
//     return <div className="h-screen min-h-[480px] bg-[var(--dark)]" />;
//   }

//   const s  = slides[idx];
//   const bg = imgSrc(s.img);
//   const titleWords = (s.title || 'Timeless Elegance').split(' ');
//   const firstWord  = titleWords[0];
//   const restWords  = titleWords.slice(1).join(' ');

//   return (
//     <div
//       onTouchStart={onTouchStart}
//       onTouchEnd={onTouchEnd}
//       className="relative h-svh min-h-[480px] md:min-h-[560px] overflow-hidden bg-[var(--dark)]"
//     >
//       {/* Background image */}
//       {bg ? (
//         <img
//           src={bg}
//           alt={s.title || ''}
//           className="absolute inset-0 w-full h-full object-cover object-center-top md:object-center opacity-65 transition-opacity duration-700"
//         />
//       ) : (
//         <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2d2420] to-[#1a1a1a]" />
//       )}

//       {/* Overlay */}
//       <div className="absolute inset-0 bg-black/40 md:bg-black/25" />

//       {/* Content */}
//       <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 md:px-10">

//         {/* Subtitle */}
//         <p className="text-[10px] md:text-[11px] tracking-[0.35em] text-white/75 uppercase font-medium mb-4 md:mb-6 font-body">
//           {s.subtitle || 'Aurora Luxury India'}
//         </p>

//         {/* Title */}
//         <h1 className="font-display font-light tracking-[-0.02em] leading-[1.05] mb-8 md:mb-12 text-[clamp(40px,12vw,72px)] md:text-[clamp(56px,10vw,110px)]">
//           <span className="block not-italic text-white">{firstWord}</span>
//           {restWords && (
//             <span className="block italic text-[var(--gold)]">{restWords}</span>
//           )}
//         </h1>

//         {/* CTA */}
//         <Link
//           to={s.ctaLink || '/shop'}
//           className="inline-block px-9 py-3.5 md:px-[52px] md:py-4 bg-transparent text-white border border-white/60 text-[10px] md:text-[11px] tracking-[0.22em] uppercase font-semibold min-w-[180px] md:min-w-0 transition-all duration-300 hover:bg-white/10 hover:border-white"
//         >
//           {s.cta || 'Explore Brands'}
//         </Link>
//       </div>

//       {/* Dots */}
//       {slides.length > 1 && (
//         <div className="absolute bottom-5 md:bottom-7 left-1/2 -translate-x-1/2 flex gap-2 z-10">
//           {slides.map((_, i) => (
//             <button
//               key={i}
//               onClick={() => setIdx(i)}
//               className="h-[2px] border-none cursor-pointer p-0 transition-all duration-300"
//               style={{
//                 width: i === idx ? 32 : 14,
//                 background: i === idx ? '#fff' : 'rgba(255,255,255,0.3)',
//               }}
//             />
//           ))}
//         </div>
//       )}

//       {/* Arrows — desktop only */}
//       {slides.length > 1 && !isMobile && (
//         <>
//           <button
//             onClick={prev}
//             onMouseEnter={() => setPaused(true)}
//             onMouseLeave={() => setPaused(false)}
//             className="absolute left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center bg-white/[0.08] border border-white/20 cursor-pointer text-white/70 transition-all duration-200 hover:bg-white/20 focus:bg-white/20"
//           >
//             <svg width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
//               <path d="M15 18l-6-6 6-6" />
//             </svg>
//           </button>

//           <button
//             onClick={next}
//             onMouseEnter={() => setPaused(true)}
//             onMouseLeave={() => setPaused(false)}
//             className="absolute right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center bg-white/[0.08] border border-white/20 cursor-pointer text-white/70 transition-all duration-200 hover:bg-white/20 focus:bg-white/20"
//           >
//             <svg width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
//               <path d="M9 18l6-6-6-6" />
//             </svg>
//           </button>
//         </>
//       )}
//     </div>
//   );
// }